import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { refreshOutline, bookmarkOutline, closeOutline } from 'ionicons/icons';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'appRedux/createStore';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { allMetrics } from './compareMetrics';

interface SettingsInlineProps {
  isOpen: boolean;
  onClose: () => void;
}

const INSIGHT_LABELS: Record<string, string> = {
  [InsightFilterEnum.ReturnToPlayer]: 'RTP',
  [InsightFilterEnum.Jackpot]: 'Jackpots',
  [InsightFilterEnum.MoneyPlayed]: '$ Played',
  [InsightFilterEnum.MoneyWon]: '$ Won',
  // Compare settings picker — Explore pill keeps "Popular".
  [InsightFilterEnum.Spins]: 'Spins',
  [InsightFilterEnum.AverageWonPerSpin]: '$ W/S',
  [InsightFilterEnum.JackpotWonPerSpin]: 'J W/S',
  [InsightFilterEnum.NonJackpotWonPerSpin]: 'W/S-J',
};

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const SettingsInline: React.FC<SettingsInlineProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);

  const { selectedInsight, selectedInsights, customDefaultInsights } = useAppSelector(
    (state) => state.app.persistedState.compareSlotsState
  );

  const appDefault = [
    InsightFilterEnum.ReturnToPlayer,
    InsightFilterEnum.Jackpot,
    selectedInsight ?? InsightFilterEnum.MoneyPlayed,
  ];

  const hasCustomDefault = Array.isArray(customDefaultInsights) && customDefaultInsights.length > 0;
  const currentDefault = hasCustomDefault ? (customDefaultInsights as string[]) : appDefault;

  const [localInsights, setLocalInsights] = useState<string[]>(
    selectedInsights?.length ? selectedInsights : currentDefault
  );

  useEffect(() => {
    if (isOpen) {
      setLocalInsights(selectedInsights?.length ? selectedInsights : currentDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = !arraysEqual(localInsights, currentDefault);

  const applyInsights = (insights: string[]) => {
    setLocalInsights(insights);
    slotCompareCommands.setSelectedInsights({ insights });
  };

  const handleToggle = (id: string) => {
    if (localInsights.includes(id)) {
      if (localInsights.length <= 1) return;
      applyInsights(localInsights.filter((s) => s !== id));
    } else {
      if (localInsights.length >= 3) return;
      applyInsights([...localInsights, id]);
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const next = [...localInsights];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    applyInsights(next);
  };

  const handleMoveDown = (index: number) => {
    if (index === localInsights.length - 1) return;
    const next = [...localInsights];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    applyInsights(next);
  };

  const handleReset = () => {
    applyInsights([...currentDefault]);
  };

  const handleSaveCustomDefault = () => {
    slotCompareCommands.setCustomDefaultInsights({ insights: [...localInsights] });
    onClose();
  };

  const handleRemoveCustomDefault = () => {
    slotCompareCommands.removeCustomDefaultInsights();
    applyInsights([...appDefault]);
  };

  const unselected = allMetrics.filter((m) => !localInsights.includes(m.id));

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="settings-inline-backdrop" onClick={onClose} />
      <div className="settings-inline" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="settings-inline__header">
          {isDirty ? (
            <button className="si-reset-btn" onClick={handleReset} aria-label="Reset">
              <IonIcon icon={refreshOutline} />
            </button>
          ) : (
            <span className="si-header-spacer" />
          )}
          <span className="settings-title">Compare Insights</span>
        </div>

        <div className="si-divider" />

        <div className="si-insight-section">
          {/* Selected insights */}
          <div className="si-insight-list">
            {localInsights.map((id, index) => (
              <div key={id} className="si-insight-row si-insight-row--selected">
                <button
                  className="si-dot-btn"
                  onClick={() => handleToggle(id)}
                  aria-label={`Deselect ${INSIGHT_LABELS[id] ?? id}`}
                >
                  <span className="si-dot si-dot--filled" />
                </button>
                <span className="si-insight-label">{INSIGHT_LABELS[id] ?? id}</span>
                <div className="si-order-arrows">
                  {index === localInsights.length - 1 && index > 0 && (
                    <button
                      className="si-arrow-btn"
                      onClick={() => handleMoveUp(index)}
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                  )}
                  {index === 0 && localInsights.length > 1 && (
                    <button
                      className="si-arrow-btn"
                      onClick={() => handleMoveDown(index)}
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="si-divider" />

          {/* Unselected insights */}
          <div className="si-insight-list">
            {unselected.map(({ id, label }) => (
              <div
                key={id}
                className={`si-insight-row si-insight-row--unselected${
                  localInsights.length >= 3 ? ' si-insight-row--disabled' : ''
                }`}
              >
                <button
                  className="si-dot-btn"
                  onClick={() => handleToggle(id)}
                  disabled={localInsights.length >= 3}
                  aria-label={`Select ${INSIGHT_LABELS[id] ?? label}`}
                >
                  <span className="si-dot si-dot--empty" />
                </button>
                <span className="si-insight-label">{INSIGHT_LABELS[id] ?? label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        {(isDirty || hasCustomDefault) && (
          <div className="si-footer">
            {isDirty && (
              <button className="si-save-btn" onClick={handleSaveCustomDefault}>
                <IonIcon icon={bookmarkOutline} />
                Save as Custom Default
              </button>
            )}
            {hasCustomDefault && (
              <>
                <button className="si-remove-btn" onClick={handleRemoveCustomDefault}>
                  <IonIcon icon={closeOutline} />
                  Remove Custom Default
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default SettingsInline;
